import React, { useState } from 'react';
import { ShiftPlanAgentPhase } from '../../utils/constants';
import { SPMSGetShiftPlanResponse, ShiftTypes, ShiftType } from '../../types/shiftPlan';

export type ShiftSearchType = ShiftType;

interface PlanSelectionMessageProps {
  phase: ShiftPlanAgentPhase;
  currentNodeId?: string | null;
  currentDate?: string | null;
  currentPlanId?: string | null;
  availablePlans?: SPMSGetShiftPlanResponse[];
  onUseCurrentPlan: () => void;
  onChooseDifferent: () => void;
  onSubmitSearch: (date: string, shiftType: ShiftSearchType) => void;
  onSelectPlan: (plan: SPMSGetShiftPlanResponse) => void;
}

const PlanSelectionMessage: React.FC<PlanSelectionMessageProps> = ({
  phase,
  currentNodeId,
  currentDate,
  currentPlanId,
  availablePlans,
  onUseCurrentPlan,
  onChooseDifferent,
  onSubmitSearch,
  onSelectPlan,
}) => {
  const [inputDate, setInputDate] = useState('');
  const [selectedShift, setSelectedShift] = useState<ShiftSearchType>(ShiftTypes.DAY_SHIFT);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputDate) {
      onSubmitSearch(inputDate, selectedShift);
    }
  };

  if (phase === ShiftPlanAgentPhase.CONFIRMING && currentPlanId) {
    return (
      <div className="plan-selection plan-selection--confirm">
        <p className="plan-selection__text">
          Using current open plan:
        </p>
        <div className="plan-selection__current-plan">
          <span className="plan-selection__plan-icon">&#128203;</span>
          <span>{currentNodeId} &middot; {currentDate} &middot; {currentPlanId}</span>
        </div>
        <div className="plan-selection__actions">
          <button className="plan-selection__btn plan-selection__btn--primary" onClick={onUseCurrentPlan}>
            Use this plan
          </button>
          <button className="plan-selection__btn plan-selection__btn--secondary" onClick={onChooseDifferent}>
            Choose a different plan
          </button>
        </div>
      </div>
    );
  }

  if (phase === ShiftPlanAgentPhase.AWAITING_INPUT) {
    return (
      <div className="plan-selection plan-selection--input">
        <p className="plan-selection__text">Which plan would you like to discuss?</p>
        <form className="plan-selection__form" onSubmit={handleSearchSubmit}>
          <div className="plan-selection__field">
            <label className="plan-selection__label" htmlFor="plan-date">Date</label>
            <input
              id="plan-date"
              className="plan-selection__input"
              type="date"
              value={inputDate}
              onChange={e => setInputDate(e.target.value)}
            />
          </div>
          <div className="plan-selection__field">
            <label className="plan-selection__label">Shift</label>
            <div className="plan-selection__shift-toggle">
              <button
                type="button"
                className={`plan-selection__shift-btn ${selectedShift === ShiftTypes.DAY_SHIFT ? 'plan-selection__shift-btn--active' : ''}`}
                onClick={() => setSelectedShift(ShiftTypes.DAY_SHIFT)}
              >
                Day
              </button>
              <button
                type="button"
                className={`plan-selection__shift-btn ${selectedShift === ShiftTypes.NIGHT_SHIFT ? 'plan-selection__shift-btn--active' : ''}`}
                onClick={() => setSelectedShift(ShiftTypes.NIGHT_SHIFT)}
              >
                Night
              </button>
            </div>
          </div>
          <button
            className="plan-selection__btn plan-selection__btn--primary"
            type="submit"
            disabled={!inputDate}
          >
            Find plans
          </button>
        </form>
      </div>
    );
  }

  if (phase === ShiftPlanAgentPhase.FETCHING_PLANS) {
    return (
      <div className="plan-selection plan-selection--loading">
        <p className="plan-selection__text">Searching for plans...</p>
        <div className="agent-typing">
          <div className="agent-typing__dot" />
          <div className="agent-typing__dot" />
          <div className="agent-typing__dot" />
        </div>
      </div>
    );
  }

  if (phase === ShiftPlanAgentPhase.SELECTING_PLAN && availablePlans) {
    if (availablePlans.length === 0) {
      return (
        <div className="plan-selection plan-selection--empty">
          <p className="plan-selection__text">No plans found for that warehouse and date. Try again.</p>
          <button className="plan-selection__btn plan-selection__btn--secondary" onClick={onChooseDifferent}>
            Try different search
          </button>
        </div>
      );
    }

    return (
      <div className="plan-selection plan-selection--list">
        <p className="plan-selection__text">
          Found {availablePlans.length} plan{availablePlans.length > 1 ? 's' : ''}:
        </p>
        <div className="plan-selection__plan-list">
          {availablePlans.map((plan, index) => (
            <button
              key={plan.planId as string || index}
              className="plan-selection__plan-item"
              onClick={() => onSelectPlan(plan)}
            >
              <div className="plan-selection__plan-item-row">
                <span className="plan-selection__plan-shift">
                  {(plan.clockWindowName as string) || 'Shift'}
                </span>
                <span className="plan-selection__plan-id">
                  {plan.planId as string}
                </span>
              </div>
              <div className="plan-selection__plan-item-row">
                <span className="plan-selection__plan-created-by">
                  {(plan.lastUpdatedBy as string) || (plan.createdBy as string) || ''}
                </span>
                <span className="plan-selection__plan-status">
                  {(plan.status as string) || (plan.planStatus as string) || ''}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default PlanSelectionMessage;
